import { Trans } from "@lingui/macro"
import { I18n } from "@lingui/core"
import { Button, DiscountChip, Spinner } from "@silversea-ssc/ui-components-react"
import clsx from "clsx"
import React, { ReactElement } from "react"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { sequenceS } from "fp-ts/Apply"
import { AirParams, FarePriceResponse } from "@ssc-booking/api-contracts"
import { useLingui } from "@lingui/react"
import { FareInfographicBoxStatus } from "../../../../../components/FareInfographicBox/FareInfographicBox"
import { AirClassOption, FareCard, FeasibleAirFares } from "../../../_common/read-models/fare-card"
import { CardPromoBadgesContent } from "../../../../_common/sanity/sanity-types"
import { ValidatedCardPromoBadges } from "../../../_common/promo-badges"
import { formatAmount } from "../../../../_common/format/localization"
import { airClassLabelFrom } from "../../../../_common/format/air-class-label"
import { isPresent, stringIsPresent } from "../../../../_common/checks"
import { toGatewaySelectItems } from "../../../_common/read-models/gateway"
import { VenetianSocietyStatus } from "../helpers"
import styles from "./FarePriceFooter.module.scss"
import AirFareToggle from "./AirFareToggle"
import GatewaySelect from "./GatewaySelect"
import FareFooterPromoBadges, { showFareFooterPromoBadges } from "./FareFooterPromoBadges"

const sequenceSO = sequenceS(O.Apply)

interface Props {
    fareCard: FareCard
    status: FareInfographicBoxStatus
    onAirFareChanged?: (option: AirClassOption) => void
    onGatewayChanged?: (gatewayCode: string) => void
    onChoose?: () => void
    promoBadges: readonly ValidatedCardPromoBadges<CardPromoBadgesContent>[]
    venetianSocietyStatus: VenetianSocietyStatus
    farePriceResponseState: FarePriceResponseState
    faresAreLoading: boolean
}

const canChangeAirFare = (props: Pick<Props, "onAirFareChanged" | "fareCard">) =>
    sequenceSO({
        options: O.fromPredicate(FeasibleAirFares.hasUpgrade)(props.fareCard.feasibleAirFares),
        onAirFareChanged: O.fromNullable(props.onAirFareChanged),
    })

type FarePriceResponseState = { type: "loading" } | { type: "loaded"; response: FarePriceResponse }

const amount = (state: FarePriceResponseState, isSelected: boolean): React.ReactElement => {
    if (state.type === "loaded") {
        // NOTE: use this to test UI -> const formattedOriginalAmount = formatAmount(Amount.build({ currency: "USD", value: 30000 }))
        const formattedOriginalAmount = F.pipe(
            state.response.price.originalTotal,
            O.matchW(F.constUndefined, formatAmount),
        )

        return (
            <span
                data-test-id="fare-card-amount"
                className={clsx(styles.priceAmount, {
                    [styles.discountedAmount]: isPresent(formattedOriginalAmount),
                })}
            >
                {formattedOriginalAmount && (
                    <React.Fragment>
                        <DiscountChip
                            lineThrough
                            background={isSelected ? "white" : "transparent"}
                            className={styles.discountChip}
                        >
                            {formattedOriginalAmount}
                        </DiscountChip>{" "}
                    </React.Fragment>
                )}
                {formatAmount(state.response.price.total)}
            </span>
        )
    }
    return <Spinner color="neutral10" size="small" />
}

const airFareByStatus = (state: FarePriceResponseState, i18n_: I18n["_"]): ReactElement | null => {
    if (state.type === "loaded") {
        const airFare = airClassLabelFrom(i18n_)(state.response.air)

        if (stringIsPresent(airFare))
            return (
                <Trans>
                    For all guests in <br />
                    {airFare}
                </Trans>
            )
    }
    return <Trans>For all guests</Trans>
}

const maybeAirFareToggle = (fareCard: FareCard, onAirFareChanged: Props["onAirFareChanged"]) =>
    F.pipe(
        canChangeAirFare({ fareCard, onAirFareChanged }),
        O.fold(F.constUndefined, (props) => (
            <div className="cy-upgrade-air-class">
                <div className={styles.actionLabel}>
                    <Trans>upgrade class airfare</Trans>
                </div>
                <AirFareToggle {...props} current={AirParams.asRequestAirFare(fareCard.air)} />
            </div>
        )),
    )

export const FarePriceFooter: React.FC<React.PropsWithChildren<Props>> = ({
    fareCard,
    status,
    onChoose,
    onAirFareChanged,
    onGatewayChanged,
    promoBadges,
    venetianSocietyStatus,
    farePriceResponseState,
    faresAreLoading,
}) => {
    const { _ } = useLingui()
    const gateways = toGatewaySelectItems(fareCard.options)

    const airFareToggle = React.useMemo(
        () => maybeAirFareToggle(fareCard, onAirFareChanged),
        [fareCard, onAirFareChanged, JSON.stringify(fareCard.air)],
    )
    const gatewaySelect =
        gateways.length > 0 && FareCard.hasFlights(fareCard) ? (
            <GatewaySelect
                gateways={gateways}
                value={AirParams.gatewayOrUndefined(fareCard.air)}
                onChange={onGatewayChanged}
            />
        ) : null

    const isSelected = status == "selected"

    return (
        <div className={styles.wrapper}>
            <div
                className={clsx(styles.footer, {
                    [styles.footerSelected]: isSelected,
                    [styles.withoutAirFareToggle]: !isPresent(airFareToggle),
                    [styles.fullBorderRadius]: !showFareFooterPromoBadges(promoBadges, venetianSocietyStatus),
                })}
            >
                {gatewaySelect && (
                    <div className={styles.gatewayContainer} data-content-name="gateways_select">
                        {gatewaySelect}
                    </div>
                )}
                {airFareToggle && (
                    <div className={styles.upgradeAirContainer} data-content-name="air_fare_toggle">
                        {airFareToggle}
                    </div>
                )}

                {airFareToggle || gatewaySelect ? <hr className={styles.separator} /> : null}

                <div className={styles.actions}>
                    <div className={styles.actionsContent}>
                        <div className={styles.priceBox}>
                            <label className={styles.fromLabel}>
                                <Trans>From</Trans>
                            </label>

                            <div className={styles.price}>{amount(farePriceResponseState, isSelected)}</div>
                            <div className={styles.pricePerGuests}>{airFareByStatus(farePriceResponseState, _)}</div>
                        </div>

                        <Button
                            className={clsx(styles.chooseButton, "cy-fare-card-choose-btn")}
                            onClick={onChoose}
                            variant="primary"
                            dataContentPiece="choose_fare"
                            disabled={faresAreLoading}
                            iconName={isSelected ? "check" : undefined}
                        >
                            {isSelected ? <Trans>Choosen</Trans> : <Trans>Choose</Trans>}
                        </Button>
                    </div>
                </div>
            </div>
            <FareFooterPromoBadges venetianSocietyStatus={venetianSocietyStatus} promoBadges={promoBadges} />
        </div>
    )
}
